"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { forwardRef, type ButtonHTMLAttributes } from "react";

const buttonVariants = cva(
  "inline-flex items-center justify-center font-mono font-bold tracking-wider uppercase transition-all duration-200 border-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          "bg-[#c5a83a] text-[#1a1a1a] border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a] hover:bg-[#d8be4e] hover:-translate-x-px hover:-translate-y-px hover:shadow-[4px_4px_0_#1a1a1a] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none",
        secondary:
          "bg-[#f5ecd6] text-[#1a1a1a] border-[#1a1a1a] shadow-[3px_3px_0_#5c4a30] hover:bg-[#fff7e6] hover:-translate-x-px hover:-translate-y-px hover:shadow-[4px_4px_0_#5c4a30] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none",
        ghost:
          "bg-transparent text-[#5c4a30] border-transparent hover:bg-[#5c4a30]/10 hover:text-[#1a1a1a] active:translate-x-px active:translate-y-px",
        danger:
          "bg-[#c54a4a] text-[#f5ecd6] border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a] hover:bg-[#d35a5a] hover:-translate-x-px hover:-translate-y-px hover:shadow-[4px_4px_0_#1a1a1a] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none",
        solid:
          "bg-[#c5a83a] text-[#1a1a1a] font-bold border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a] hover:bg-[#d8be4e] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none",
      },
      size: {
        sm: "text-xs px-3 py-1.5 rounded",
        md: "text-sm px-5 py-2.5 rounded-md",
        lg: "text-base px-7 py-3.5 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={clsx(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
